import { useEffect } from 'react';

import { hardRedirectTo } from '../../../utils';
import type { MfaMethodType } from '../../../../shared/model-typings/mfaMethod';
import type { LoginResponse } from '../../../../shared/types/routes/auth/v2';

/**
 * React to a settled login response — the shared outcome of the credential (`POST /api/auth/login/credentials`)
 * and social-complete (`POST /api/auth/login/social/complete`) flows. An `mfa_required` result hands off to the
 * MFA step; a `logged_in` result hard-redirects into the app.
 *
 * Runs from an effect rather than the mutate `onSuccess` callback: React Query invokes that callback before
 * notifying its subscribers, so navigating there unmounts the calling component while React Query is still about to
 * update it — which warns about a state update on an unmounted component under React 17. Reacting to the settled
 * `data` defers the hand-off until after the component has re-rendered.
 */
export function useLoginResponseEffect(
  data: LoginResponse | undefined,
  onMfaRequired: (mfaMethod: MfaMethodType) => void,
): void {
  useEffect(() => {
    if (!data) {
      return;
    }
    if (data.status === 'mfa_required') {
      onMfaRequired(data.mfaMethod);
      return;
    }
    hardRedirectTo(data.to ?? '/');
  }, [data, onMfaRequired]);
}
